import React, {ReactElement} from 'react';
import SignaturePad from 'react-signature-pad-wrapper';
import {useTranslation} from 'react-i18next';
import {useFormContext} from 'react-hook-form';
import {GUEST_FORM_NAMES, SessionStorage, useTimeoutRef} from '@chekin/core';
import {SIGNATURES_QUALITY, STORAGE_KEYS} from 'utils/constants';
import {useGetGuestId} from 'hooks/useGetGuestId';
import {useSchema} from 'context/guest';
import {useReservation} from 'context/reservation';
import {useContracts, useGuest} from 'hooks';
import {useSignatureState} from './useSignatureState';
import {trimCanvas} from 'utils/trimCanvas';
import {fakeSignature} from './index';
import {Button} from '@chekinapp/ui';
import {DEVICE, useModalControls, useScreenResize, cn} from '@chekin/guestapp-ui-kit';
import {KIOSKO_BUTTON_CLASSES} from 'utils/kioskoClasses';
import ChekinProSettings from 'ChekinProSettings';
import SignatureModal from './SignatureModal';
import SignatureCanvas from 'components/common/SignatureCanvas';
import {ModalSheet} from 'components/common/ModalSheet/ModalSheet';
import {ContractBox} from './ContractBox';
import {FieldsGroup} from 'components/FieldsGroup';
import {AirbnbSubmitDisclosure} from './AirbnbSubmitDisclosure';
import {
  Wrapper,
  SignButton,
  SubmitButton,
  ErrorMessage,
  DesktopSignArea,
  DesktopSignatureImage,
  RepeatSignatureButton,
  DesktopSignAreaContainer,
} from './styled';

export type SignBlockProps = {
  onSubmit: (signature: string) => void;
  openDataIncompleteModalOpen: () => void;
  hasSignature?: boolean;
  automatedSignature?: boolean;
  isSignatureRequired?: boolean;
  agreementClauses?: ReactElement;
};

const SignBlock = ({
  onSubmit,
  hasSignature,
  automatedSignature,
  isSignatureRequired,
  openDataIncompleteModalOpen,
  agreementClauses,
}: SignBlockProps) => {
  const {t} = useTranslation();
  const isAirbnbVariant = ChekinProSettings.appVariant === 'airbnb';
  const signatureRef = React.useRef<SignaturePad | null>(null);
  const {reservationId} = useReservation();
  const guestId = useGetGuestId();
  const {guest} = useGuest({id: guestId});
  const {isGuestSchemaLoading, isGuestSchemaLoaded, formFields} = useSchema();
  const hasFormFields = Boolean(Object.keys(formFields)?.length);
  const {isMatch: isMobile} = useScreenResize(DEVICE.laptop);
  const timeoutRef = useTimeoutRef();
  const {signature, setSignature, resetSignature} = useSignatureState();
  const [isSignatureEditEnabled, setIsSignatureEditEnabled] = React.useState(false);
  const [isSignatureRequiredError, setIsSignatureRequiredError] = React.useState(false);
  const {trigger, watch} = useFormContext();

  const {contracts, isContractsError, isLoadingContracts} = useContracts();

  const {
    isOpen: isSignatureModal,
    openModal: openSignatureModal,
    closeModal: closeSignatureModal,
  } = useModalControls();

  const handleOpenSignatureModal = React.useCallback(() => {
    resetSignature();
    setIsSignatureEditEnabled(true);
    setIsSignatureRequiredError(false);
    openSignatureModal();
  }, [openSignatureModal, resetSignature]);

  React.useEffect(
    function preloadPersistedLocationSignature() {
      const storageSignature = SessionStorage.get<string>(
        STORAGE_KEYS.personalSignatureFormData,
      );
      const loadedSignature = guest?.id ? guest?.signature : storageSignature;
      if (!isSignatureModal && loadedSignature && reservationId && isGuestSchemaLoaded) {
        timeoutRef.current = setTimeout(() => {
          signatureRef.current?.fromDataURL(loadedSignature, {
            width: 319,
            height: 182,
          });

          setIsSignatureEditEnabled(true);
          setSignature(loadedSignature);
        }, 100); // improve
      }
    },
    [
      setSignature,
      reservationId,
      isGuestSchemaLoaded,
      guest?.id,
      guest?.signature,
      isSignatureModal,
      timeoutRef,
    ],
  );

  const handleSignatureEnable = React.useCallback(() => {
    setIsSignatureEditEnabled(true);
  }, []);

  const handleChange = () => {
    const signatureInstance = signatureRef.current;
    const canvas = signatureInstance?.canvas?.current;
    if (!canvas) return;
    const signature = trimCanvas(canvas)?.toDataURL(undefined, SIGNATURES_QUALITY);
    const isEmpty = signatureInstance?.isEmpty();

    if (signature && !isEmpty) {
      setSignature(signature);
    }
  };

  const handleClear = () => {
    signatureRef.current?.clear();
    resetSignature();
  };

  const handleSubmit = () => {
    if (!hasFormFields) return;

    const showSignModal = isSignatureEditEnabled && !signature && hasSignature;

    if (!showSignModal && !hasSignature && !automatedSignature) {
      onSubmit(fakeSignature);
      return;
    }

    if (!signature && hasSignature && !automatedSignature && isSignatureRequired) {
      setIsSignatureRequiredError(!!isSignatureRequired);
      openDataIncompleteModalOpen();
      void trigger();
      return;
    }

    if (!showSignModal) {
      onSubmit(signature);
    }
  };

  return (
    <>
      <Wrapper className="sign-block">
        {hasSignature && !automatedSignature && (
          <FieldsGroup title={t('signature')}>
            <DesktopSignAreaContainer
              onClick={handleOpenSignatureModal}
              className="sign-block__area kiosko:w-full kiosko:max-w-full kiosko:h-[450px]"
              $required={isSignatureRequiredError}
              $disabled={isGuestSchemaLoading}
            >
              {!signature && !isSignatureModal && (
                <DesktopSignArea>
                  {!isGuestSchemaLoading && (
                    <SignButton className="kiosko:text-kiosko-s">
                      {isMobile ? t('tap_here_to_sign') : t('click_here_to_sign')}
                    </SignButton>
                  )}
                </DesktopSignArea>
              )}
              {signature && !isSignatureModal && (
                <DesktopSignatureImage src={signature} />
              )}
            </DesktopSignAreaContainer>

            {signature && !isSignatureModal && (
              <RepeatSignatureButton
                className="sign-block__repeat-btn"
                onClick={handleOpenSignatureModal}
              >
                {t('repeat_signature')}
              </RepeatSignatureButton>
            )}
          </FieldsGroup>
        )}
        {isSignatureRequiredError && (
          <ErrorMessage className="sign-block__error">
            {t('the_signature_is_required')}
          </ErrorMessage>
        )}
        {agreementClauses}
        {isAirbnbVariant ? (
          <AirbnbSubmitDisclosure className="col-span-full w-full mt-1 mb-4" />
        ) : (
          <ContractBox
            contracts={contracts}
            hasError={isContractsError}
            isLoading={isLoadingContracts}
            defaultEmail={watch(GUEST_FORM_NAMES.email)}
            isAutomatedSignature={hasSignature && automatedSignature}
          />
        )}
        <SubmitButton>
          <Button
            onClick={handleSubmit}
            size="md"
            className={cn('m-auto submit-btn sm:max-w-[275px]', KIOSKO_BUTTON_CLASSES)}
          >
            {t('submit')}
          </Button>
        </SubmitButton>
      </Wrapper>
      <ModalSheet
        isOpenModalSheet={isSignatureModal}
        handleCloseModal={closeSignatureModal}
        maxWidth={'900px'}
        withCloseButton
        closeButtonType="click"
      >
        <SignatureModal
          hasSignature={Boolean(signature)}
          onClear={handleClear}
          onChange={handleChange}
          onClose={closeSignatureModal}
          signatureArea={
            <SignatureCanvas
              ref={signatureRef}
              onEnd={handleChange}
              onClear={handleClear}
              enabled={isSignatureEditEnabled}
              onEnable={handleSignatureEnable}
              hasSignature={Boolean(signature)}
            />
          }
        />
      </ModalSheet>
    </>
  );
};

export {SignBlock};
